import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import algoliasearchv4, {AlgoliaSearchOptions} from 'algoliasearch';
import {RequestService} from 'src/app/data/handlers/request/request.service';
import {Observable} from 'rxjs';
import {AlgoliaBrowseInterface, AlgoliaSearchInterface} from '../../models/algolia/requests/algolia';
import {countryConfig} from '../../../../../country-config/country-config';
import {globalEnv} from '../../connection/global/global.env';
import {algoliaEnv} from '../../../../../environments/colombia/algolia.env';
import {LocalStorageManager} from 'src/app/data/manager/local-storage/local-storage.manager';
import {isPlatformBrowser} from '@angular/common';
import {userTokenAlgolia} from 'src/app/ui/algolia/algoliaInit';
import { ConfigService } from '../../../networking/services-backend-multicountry/config/config.service';
import { PropertiesTypes } from '../../services-backend-multicountry/models/properties/propertiesModel.type';
import { LocationManager } from 'src/app/data/manager/location/location.manager';

@Injectable({
  providedIn: 'root',
})
export class AlgoliaService {
  isBrowser = false;

  constructor(
    private req: RequestService,
    private lsm: LocalStorageManager,
    @Inject(PLATFORM_ID) platformId,
    private configService: ConfigService,
    private location: LocationManager,
  ) {
    this.isBrowser =
      isPlatformBrowser(platformId) && !(navigator.userAgent.indexOf('Chrome-Lighthouse') > -1);
  }

  env = countryConfig.algolia;
  algoliaSearchOptions: AlgoliaSearchOptions | null = this.env.hostTopsort
    ? {
        hosts: [{ url: this.env.hostTopsort },{ url: this.env.hostAlgolia }] ,
        timeouts: {
          connect: 5,
          read: 5,
          write: 30,
        }
     }
    : null;
  client = algoliasearchv4(this.env.id, this.env.key, this.algoliaSearchOptions);
  index = this.client.initIndex(this.env.index);
  indexSub = this.client.initIndex(this.env.indexSub);
  properties = countryConfig.algolia.properties;
  public user: string;

  searchMultiQuery(data: AlgoliaSearchInterface, id, objSeo?: AlgoliaSearchInterface) {
    let ind = this.client.initIndex(this.env.index);
    return ind.browseObjects(data);
  }

  searchBrowseSeo(data: AlgoliaSearchInterface) {
    let ind = this.client.initIndex(this.env.items_seo);
    return ind.browseObjects(data);
  }

  searchText(data: AlgoliaSearchInterface, userSearch?, headers?) {
    let objSend = Object.assign(data);

    objSend.indexName = objSend.indexName ? objSend.indexName : this.env.index;
    const query = objSend;
    if (userSearch) {
      query.params.clickAnalytics = true;
      if (this.isBrowser) {
        const customer = this.lsm.get('customer');
        this.user = customer?.analyticsUUID || userTokenAlgolia;
        query.params.userToken = this.user;
      }
    } else {
      query.params.clickAnalytics = false;
    }

    const options = {
      headers: headers || {},
    };

    return this.client.multipleQueries([query], options);
  }

  searchTextBrowse(data: AlgoliaBrowseInterface) {
    let ind = this.client.initIndex(this.env.index);
    return ind.browseObjects(data);
  }

  objectSearch(objectId, index?) {
    if (index) {
      this.index = this.client.initIndex(index);
    }
    return this.index.getObjects(objectId);
  }

  getSeo(id): Observable<any> {
    const headers = {
      'X-Algolia-API-Key': algoliaEnv.envs.prod.key,
      'X-Algolia-Application-Id': algoliaEnv.envs.prod.id,
      noSendCountry: true,
    };
    return this.req.get(
      `${globalEnv.alg}1/indexes/${countryConfig.algolia.items_seo}/${id}`,
      null,
      null,
      null,
      null,
      false,
      headers,
    );
  }

  algoliaConfigurationPaymentMethod(): Observable<any> {
    return this.getCustomPropertieAlgolia('CREDIT_CARD.CREATION_CONFIG_DEV');
  }

  getCustomPropertieAlgolia(propertieId: string): Observable<PropertiesTypes> {
    return this.configService.getProperty<PropertiesTypes>(propertieId)
  }

  getPropertiesAlgolia( objectID ): Observable<any> {
    return this.req.get(`${globalEnv.utilities}algolia/v1/properties`, {objectID}, null, false);
  }

  primeBrowse(data: AlgoliaBrowseInterface) {
    let ind = this.client.initIndex('properties');
    return ind.browseObjects(data);
  }

  getProduct(objectID): Observable<any> {
    return this.req.get(`${globalEnv.utilities}algolia/v1/products`, {objectID}, null, true);
  }

  getLandingsAlgolia(objectID): Observable<any> {
    return this.req.get(`${globalEnv.utilities}algolia/v1/landing_pages`, {objectID}, null, false);
  }

  getObjectsIds(objectsArray, customIndex = false): Promise<any> {
    if (customIndex) {
      return this.indexSub.getObjects(objectsArray).then(({results}) => results);
    }

    return this.index.getObjects(objectsArray).then(({results}) => results);
  }

  getObjectsIdsByProperties(objectsArray): Promise<any> {
    return this.client
      .initIndex(this.properties)
      .getObject(objectsArray)
      .then(results => results);
  }

  getObjectsTutorials(): Observable<any> {
    const indexName = 'prod_video';
    const params = 'x-algolia-agent=Algolia%20for%20JavaScript%20(4.5.1)%3B%20Browser';

    const headers = {
      'X-Algolia-API-Key': algoliaEnv.envs.prod.key,
      'X-Algolia-Application-Id': algoliaEnv.envs.prod.id,
      noSendCountry: true,
    };

    return this.req.get(
      `${globalEnv.alg}1/indexes/${indexName}/browse?${params}`,
      null,
      null,
      null,
      null,
      false,
      headers,
    );
  }

  getObjectsProductDetail(objectId, index): Promise<any> {

    const city = this.getIdCity();

    const nearbyStores = this.location.getNearbyStores();

    const options = {
      headers: {
        'Content-Type': 'application/json',
        'X-Custom-City': city,
        'X-Nearby-Stores': nearbyStores.join(','),
      }
    }


    const ind = this.client.initIndex(this.env[index]);
    return ind.getObject(objectId,options).then(hits => hits);
  }

  getIdCity(): string {
    return this.location.city.id || this.location.city.deliveryType || countryConfig.defaultCity;
  }

}
